import React, { useEffect, useRef, useState } from 'react';
import { experiences } from '../data/mock';
import { MapPin, ExternalLink, Calendar } from 'lucide-react';

// Animated gradient underline component
const AnimatedUnderline = ({ isVisible, delay = 0 }) => (
  <div className="relative h-1 mt-4 overflow-hidden">
    <div
      className={`absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-400 to-gray-200 dark:from-white dark:via-gray-400 dark:to-gray-700 transition-all duration-1000 ${isVisible ? 'w-32 opacity-100' : 'w-0 opacity-0'
        }`}
      style={{ transitionDelay: `${delay}ms` }}
    />
    {/* Animated shimmer overlay */}
    <div
      className={`absolute inset-0 w-32 overflow-hidden ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ transitionDelay: `${delay + 500}ms` }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
    </div>
  </div>
);

const ExperienceCard = ({ experience, index, activeIndex, setActiveIndex }) => {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const isActive = activeIndex === index;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Auto-highlight when card enters viewport
          setActiveIndex(index);
        }
      },
      { threshold: 0.4 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index, setActiveIndex]);

  return (
    <div
      ref={cardRef}
      className={`relative transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-16 scale-95'
        }`}
      style={{ transitionDelay: `${index * 150}ms` }}
      onMouseEnter={() => setActiveIndex(index)}
    >
      {/* Card */}
      <div className={`relative rounded-2xl p-6 md:p-8 transition-all duration-500 border ${isActive
        ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 shadow-xl dark:shadow-2xl scale-[1.02]'
        : 'bg-gray-50/50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
        }`}>

        {/* Active indicator line with shimmer */}
        <div className={`absolute left-0 top-6 bottom-6 w-1 rounded-full transition-all duration-500 overflow-hidden ${isActive
          ? 'bg-gradient-to-b from-gray-800 via-gray-500 to-gray-300 dark:from-white dark:via-gray-400 dark:to-gray-600 opacity-100'
          : 'bg-gray-200 dark:bg-gray-700 opacity-0'
          }`}>
          {isActive && (
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent animate-shimmer" />
          )}
        </div>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-5 pl-4">
          <div>
            <h3 className={`text-xl md:text-2xl font-light transition-all duration-500 ${isActive ? 'text-black dark:text-white translate-x-1' : 'text-gray-600 dark:text-gray-400 translate-x-0'
              }`}>
              {experience.company}
            </h3>
            <p className={`text-base font-light transition-all duration-500 ${isActive ? 'text-gray-600 dark:text-gray-300 translate-x-1' : 'text-gray-400 dark:text-gray-500 translate-x-0'
              }`}>
              {experience.role}
            </p>
          </div>

          <div className="flex items-center gap-3 pl-4 lg:pl-0">
            <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
              <Calendar size={14} />
              <span>{experience.period}</span>
            </div>
            {experience.location && (
              <div className="flex items-center gap-1 text-sm text-gray-400 dark:text-gray-500">
                <MapPin size={12} />
                <span>{experience.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* Responsibilities */}
        <div className={`space-y-2.5 pl-4 transition-all duration-500 ${isActive ? 'opacity-100 max-h-[1000px]' : 'opacity-60 max-h-[1000px]'
          }`}>
          {experience.description.map((item, i) => (
            <div
              key={i}
              className={`flex items-start gap-3 transition-all duration-500 ${isActive ? 'translate-x-1' : 'translate-x-0'
                }`}
              style={{ transitionDelay: `${i * 50}ms` }}
            >
              <div className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 transition-all duration-500 ${isActive ? 'bg-gray-400 dark:bg-gray-500 scale-125' : 'bg-gray-300 dark:bg-gray-600 scale-100'
                }`} />
              <p className={`text-[15px] font-light leading-relaxed transition-all duration-500 ${isActive ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'
                }`}>
                {item}
              </p>
            </div>
          ))}
        </div>

        {/* Links */}
        {experience.links && experience.links.length > 0 && (
          <div className={`mt-5 pt-5 border-t pl-4 flex flex-wrap gap-3 transition-all duration-300 ${isActive ? 'border-gray-100 dark:border-gray-700' : 'border-transparent'
            }`}>
            {experience.links.map((link) => (
              <a
                key={link}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-700/50 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-300"
              >
                <ExternalLink size={12} />
                {link.replace('https://', '').replace(/\/$/, '')}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Experience = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="experience"
      ref={sectionRef}
      className="py-14 md:py-20 bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 transition-colors duration-500 relative overflow-hidden"
    >
      {/* Animated background decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-gray-200/40 via-gray-100/20 to-transparent dark:from-gray-700/30 dark:via-gray-800/15 rounded-full blur-3xl animate-morph opacity-40" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tl from-gray-300/30 to-transparent dark:from-gray-600/20 rounded-full blur-3xl animate-morph-reverse opacity-30" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        {/* Section Title - matching About Me style */}
        <h2
          className={`text-4xl md:text-5xl lg:text-6xl font-extralight text-black dark:text-white transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
        >
          Experience
        </h2>

        {/* Animated gradient underline */}
        <AnimatedUnderline isVisible={isVisible} delay={200} />

        {/* Experience Cards */}
        <div className="mt-12 space-y-4">
          {experiences.map((experience, index) => (
            <ExperienceCard
              key={experience.company}
              experience={experience}
              index={index}
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
